import type { CV, Experience, Education, Language } from '../../types/cv'
import { nanoid } from 'nanoid'

export interface ExtractedCV {
  cv: Partial<CV>
  confidence: number
  rawText: string
  detectedLanguage?: 'es' | 'en' | 'fr' | 'pt' | 'de'
}

// Basic regex patterns for CV extraction
const patterns = {
  email: /[\w.-]+@[\w.-]+\.\w+/gi,
  phone: /(?:\+?\d{1,3}[-.\s]?)?\(?\d{2,4}\)?[-.\s]?\d{3,4}[-.\s]?\d{3,4}/g,
  linkedin: /linkedin\.com\/in\/[\w-]+/gi,
  github: /github\.com\/[\w-]+/gi,
  url: /https?:\/\/[^\s]+/gi,
}

// Common section headers in Spanish and English
const sectionHeaders = {
  experience: [
    'experiencia',
    'experience',
    'experiencia laboral',
    'work experience',
    'employment history',
    'historial laboral',
  ],
  education: [
    'educacion',
    'education',
    'formacion',
    'formación académica',
    'academic background',
    'estudios',
  ],
  skills: [
    'habilidades',
    'skills',
    'competencias',
    'conocimientos',
    'technical skills',
    'aptitudes',
  ],
  languages: [
    'idiomas',
    'languages',
    'lenguajes',
  ],
  summary: [
    'resumen',
    'summary',
    'perfil',
    'profile',
    'sobre mi',
    'about me',
    'objetivo',
    'objective',
  ],
}

function findSectionContent(text: string, headers: string[]): string | null {
  const lowerText = text.toLowerCase()

  for (const header of headers) {
    const regex = new RegExp(`${header}[:\\s]*([\\s\\S]*?)(?=\\n\\n|$)`, 'i')
    const match = lowerText.match(regex)
    if (match && match[1]) {
      // Find the actual position in original text
      const startIndex = text.toLowerCase().indexOf(header)
      if (startIndex !== -1) {
        // Get content after the header until next section or end
        const afterHeader = text.substring(startIndex + header.length)
        const nextSectionMatch = afterHeader.match(/\n\s*[A-Z][A-Za-z\s]+:\s*\n/m)
        if (nextSectionMatch && nextSectionMatch.index) {
          return afterHeader.substring(0, nextSectionMatch.index).trim()
        }
        return afterHeader.substring(0, 500).trim()
      }
    }
  }
  return null
}

function extractEmail(text: string): string | undefined {
  const matches = text.match(patterns.email)
  return matches?.[0]
}

function extractPhone(text: string): string | undefined {
  const matches = text.match(patterns.phone)
  return matches?.[0]
}

function extractLinks(text: string): { label: string; url: string; id: string }[] {
  const links: { label: string; url: string; id: string }[] = []

  const linkedinMatches = text.match(patterns.linkedin)
  if (linkedinMatches) {
    links.push({
      id: nanoid(),
      label: 'LinkedIn',
      url: `https://${linkedinMatches[0]}`,
    })
  }

  const githubMatches = text.match(patterns.github)
  if (githubMatches) {
    links.push({
      id: nanoid(),
      label: 'GitHub',
      url: `https://${githubMatches[0]}`,
    })
  }

  return links
}

function extractName(text: string): string | undefined {
  // Usually the name is at the beginning and is in larger font (appears first)
  const lines = text.split('\n').filter((line) => line.trim())
  if (lines.length > 0) {
    const firstLine = lines[0].trim()
    // If the first line looks like a name (2-4 words, capitalized)
    if (firstLine.split(' ').length >= 2 && firstLine.split(' ').length <= 5) {
      const words = firstLine.split(' ')
      const looksLikeName = words.every(
        (word) => word.length > 0 && word[0] === word[0].toUpperCase()
      )
      if (looksLikeName && firstLine.length < 50) {
        return firstLine
      }
    }
  }
  return undefined
}

function extractTitle(text: string, name: string | undefined): string | undefined {
  const lines = text.split('\n').filter((line) => line.trim())
  const nameIndex = name ? lines.findIndex((l) => l.includes(name)) : -1

  // Title is usually right after the name
  if (nameIndex >= 0 && lines[nameIndex + 1]) {
    const potentialTitle = lines[nameIndex + 1].trim()
    // Filter out emails, phones, etc.
    if (
      potentialTitle.length < 100 &&
      !potentialTitle.match(patterns.email) &&
      !potentialTitle.match(patterns.phone)
    ) {
      return potentialTitle
    }
  }
  return undefined
}

function parseExperience(text: string | null): Experience[] {
  if (!text) return []

  const experiences: Experience[] = []
  // Simple heuristic: split by date patterns or job-looking entries
  const datePattern = /\d{4}\s*[-–]\s*(?:\d{4}|presente|actual|current)/gi

  // This is a basic extractor - the AI-powered version will be much better
  const lines = text.split('\n').filter((l) => l.trim())

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    if (line.match(datePattern)) {
      experiences.push({
        id: nanoid(),
        position: lines[i - 1]?.trim() || '',
        company: lines[i - 2]?.trim() || '',
        description: lines[i + 1]?.trim() || '',
        current: line.toLowerCase().includes('presente') || line.toLowerCase().includes('current'),
      })
    }
  }

  return experiences.slice(0, 5) // Limit to 5 entries
}

function parseEducation(text: string | null): Education[] {
  if (!text) return []

  const education: Education[] = []
  const lines = text.split('\n').filter((l) => l.trim())

  // Look for university/institution names and degrees
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].toLowerCase()
    if (
      line.includes('universidad') ||
      line.includes('university') ||
      line.includes('instituto') ||
      line.includes('college')
    ) {
      education.push({
        id: nanoid(),
        institution: lines[i].trim(),
        degree: lines[i + 1]?.trim() || '',
        field: '',
        current: false,
      })
    }
  }

  return education.slice(0, 3) // Limit to 3 entries
}

function parseLanguages(text: string | null): Language[] {
  if (!text) return []

  const languages: Language[] = []
  const lines = text.split('\n').filter((l) => l.trim())

  const commonLanguages = [
    'español',
    'spanish',
    'inglés',
    'english',
    'francés',
    'french',
    'alemán',
    'german',
    'portugués',
    'portuguese',
    'italiano',
    'italian',
    'chino',
    'chinese',
    'japonés',
    'japanese',
  ]

  for (const line of lines) {
    const lowerLine = line.toLowerCase()
    for (const lang of commonLanguages) {
      if (lowerLine.includes(lang)) {
        let level: Language['level'] = 'intermediate'
        if (lowerLine.includes('nativo') || lowerLine.includes('native')) {
          level = 'native'
        } else if (lowerLine.includes('fluent') || lowerLine.includes('avanzado')) {
          level = 'fluent'
        } else if (lowerLine.includes('básico') || lowerLine.includes('basic')) {
          level = 'basic'
        }

        // Capitalize first letter
        const langName = lang.charAt(0).toUpperCase() + lang.slice(1)
        if (!languages.find((l) => l.name?.toLowerCase() === lang)) {
          languages.push({
            id: nanoid(),
            name: langName,
            level,
          })
        }
      }
    }
  }

  return languages.slice(0, 5)
}

/**
 * Basic CV extraction using regex patterns - used as fallback when AI fails
 */
export function extractCVFromTextBasic(rawText: string): ExtractedCV {
  const text = rawText.trim()

  // Extract personal info
  const name = extractName(text)
  const title = extractTitle(text, name)
  const email = extractEmail(text)
  const phone = extractPhone(text)
  const links = extractLinks(text)

  // Extract sections
  const summaryText = findSectionContent(text, sectionHeaders.summary)
  const experienceText = findSectionContent(text, sectionHeaders.experience)
  const educationText = findSectionContent(text, sectionHeaders.education)
  const languagesText = findSectionContent(text, sectionHeaders.languages)

  // Parse structured data
  const experience = parseExperience(experienceText)
  const education = parseEducation(educationText)
  const languages = parseLanguages(languagesText)

  // Calculate confidence based on what we found
  let confidence = 0
  if (name) confidence += 20
  if (email) confidence += 15
  if (title) confidence += 15
  if (summaryText) confidence += 15
  if (experience.length > 0) confidence += 20
  if (education.length > 0) confidence += 10
  if (languages.length > 0) confidence += 5

  const cv: Partial<CV> = {
    personalInfo: {
      fullName: name || '',
      title: title || '',
      email: email || '',
      phone: phone || '',
      location: '',
      links,
    },
    summary: summaryText?.substring(0, 500) || '',
    experience,
    education,
    skills: {
      technical: [],
      soft: [],
      tools: [],
    },
    languages,
    certifications: [],
    projects: [],
  }

  return {
    cv,
    confidence,
    rawText,
  }
}

/**
 * Converts AI response (ParsePdfResponse) to ExtractedCV format
 */
export function convertAIResponseToExtractedCV(
  response: {
    extractedData: {
      personalInfo?: {
        fullName?: string
        title?: string
        email?: string
        phone?: string
        location?: string
      }
      summary?: string
      experience?: Array<{
        company?: string
        position?: string
        startDate?: string
        endDate?: string
        description?: string
      }>
      education?: Array<{
        institution?: string
        degree?: string
        field?: string
        startDate?: string
        endDate?: string
      }>
      skills?: string[]
      languages?: Array<{
        name?: string
        level?: string
      }>
    }
    rawText: string
    confidence: number
    detectedLanguage?: string
  }
): ExtractedCV {
  const { extractedData, rawText, confidence, detectedLanguage } = response

  // Validate and normalize detected language
  const validLanguages = ['es', 'en', 'fr', 'pt', 'de'] as const
  const normalizedLanguage = validLanguages.includes(detectedLanguage as typeof validLanguages[number])
    ? (detectedLanguage as typeof validLanguages[number])
    : undefined

  // Convert experience with IDs
  const experience: Experience[] = (extractedData.experience || []).map((exp) => ({
    id: nanoid(),
    company: exp.company || '',
    position: exp.position || '',
    startDate: exp.startDate || '',
    endDate: exp.endDate || '',
    description: exp.description || '',
    current: !exp.endDate || exp.endDate.toLowerCase().includes('present') || exp.endDate.toLowerCase().includes('actual'),
  }))

  // Convert education with IDs
  const education: Education[] = (extractedData.education || []).map((edu) => ({
    id: nanoid(),
    institution: edu.institution || '',
    degree: edu.degree || '',
    field: edu.field || '',
    startDate: edu.startDate || '',
    endDate: edu.endDate || '',
    current: !edu.endDate || edu.endDate.toLowerCase().includes('present') || edu.endDate.toLowerCase().includes('actual'),
  }))

  // Convert languages with IDs and proper level mapping
  const languages: Language[] = (extractedData.languages || []).map((lang) => {
    let level: Language['level'] = 'intermediate'
    const lvl = (lang.level || '').toLowerCase()
    if (lvl.includes('nativ')) level = 'native'
    else if (lvl.includes('fluent') || lvl.includes('fluid')) level = 'fluent'
    else if (lvl.includes('advanced') || lvl.includes('avanzado')) level = 'advanced'
    else if (lvl.includes('basic') || lvl.includes('básico') || lvl.includes('basico')) level = 'basic'

    return {
      id: nanoid(),
      name: lang.name || '',
      level,
    }
  })

  const cv: Partial<CV> = {
    personalInfo: {
      fullName: extractedData.personalInfo?.fullName || '',
      title: extractedData.personalInfo?.title || '',
      email: extractedData.personalInfo?.email || '',
      phone: extractedData.personalInfo?.phone || '',
      location: extractedData.personalInfo?.location || '',
      links: [],
    },
    summary: extractedData.summary || '',
    experience,
    education,
    skills: {
      technical: extractedData.skills || [],
      soft: [],
      tools: [],
    },
    languages,
    certifications: [],
    projects: [],
  }

  return {
    cv,
    confidence,
    rawText,
    detectedLanguage: normalizedLanguage,
  }
}

/**
 * @deprecated Use extractCVFromTextBasic for fallback extraction
 */
export const extractCVFromText = extractCVFromTextBasic
